// src/pages/admin/Education/Education.jsx
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  FaEye, 
  FaEdit, 
  FaTrash, 
  FaPlus, 
  FaSearch, 
  FaTimes,
  FaGraduationCap,
  FaCalendarAlt,
  FaMoneyBillWave,
  FaStar,
  FaCheckCircle,
  FaSpinner,
  FaFilter,
  FaUsers,
  FaUserGraduate,
} from 'react-icons/fa';
import { useDispatch, useSelector } from "react-redux";
import { successToast, errorToast } from '../../Utils/toast';
import { openModal } from "../../redux/features/modalSlice";
import Pagination from '../../common/Pagination';
import SearchBar from '../../common/Search';
import { setEducationFilters } from '../../redux/features/filtersSlice';
import { 
  useGetAdminScholarshipsQuery,
  useGetScholarshipStatisticsQuery,
  useUpdateScholarshipStatusMutation,
  useDeleteScholarshipMutation,
} from "../../redux/services/scholarshipsService/scholarships.Service";
import CommonBreadcrumb from '../../common/CommonBreadcrumb';
 
function Education() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const filter = useSelector((state) => state.filters?.education || {});
  
  const [statusFilter, setStatusFilter] = useState(filter.status || '');
  const [categoryFilter, setCategoryFilter] = useState(filter.category || '');
  const [typeFilter, setTypeFilter] = useState(filter.type || '');
  const [loadingId, setLoadingId] = useState(null);

  // API Hooks
  const { data, isLoading, isError, refetch } = useGetAdminScholarshipsQuery({
    page: filter.page || 1,
    limit: filter.limit || 10,
    status: statusFilter,
    category: categoryFilter,
    type: typeFilter,
    search: filter.search || '',
  });

  const { data: statsData } = useGetScholarshipStatisticsQuery();
  const [updateStatus] = useUpdateScholarshipStatusMutation();
  const [deleteScholarship] = useDeleteScholarshipMutation();

  const scholarships = data?.data || [];
  const pagination = data?.pagination || {};
  const totalPages = pagination?.totalPages || 0;
  const totalItems = pagination?.total || 0;
  const currentPage = pagination?.page || 1;
  const showPagination = totalPages > 1;
  const stats = statsData?.data || {};

  // Get status badge
  const getStatusBadge = (status) => {
    const statusMap = {
      'pending': { class: 'status-pending', label: 'Pending' },
      'approved': { class: 'status-approved', label: 'Approved' },
      'rejected': { class: 'status-rejected', label: 'Rejected' },
      'expired': { class: 'status-expired', label: 'Expired' },
    };
    const statusInfo = statusMap[status] || statusMap['pending'];
    return <span className={`education-status-badge ${statusInfo.class}`}>
      {statusInfo.label}
    </span>;
  };

  // Get category label
  const getCategoryLabel = (category) => {
    const categoryMap = {
      'scholarship': 'Scholarship',
      'education_loan': 'Education Loan',
      'career_guidance': 'Career Guidance',
      'skill_development': 'Skill Development',
      'study_abroad': 'Study Abroad',
    };
    return categoryMap[category] || category || 'N/A';
  };

  // Get type label
  const getTypeLabel = (type) => {
    const typeMap = {
      'government': 'Government',
      'private': 'Private',
      'trust': 'Trust',
      'corporate': 'Corporate',
      'international': 'International',
    };
    return typeMap[type] || type || 'N/A';
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Handle page change
  const handlePageChange = (page) => {
    dispatch(
      setEducationFilters({
        page,
      })
    );
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle search
  const handleSearch = (value) => {
    dispatch(
      setEducationFilters({
        search: value,
        page: 1,
      })
    );
  };

  // Handle limit change
  const handleLimitChange = (e) => {
    const newLimit = parseInt(e.target.value);
    dispatch(
      setEducationFilters({
        limit: newLimit,
        page: 1,
      })
    );
  };

  // Handle status filter
  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    dispatch(
      setEducationFilters({
        page: 1,
      })
    );
  };

  // Handle category filter
  const handleCategoryFilter = (category) => {
    setCategoryFilter(category);
    dispatch(
      setEducationFilters({
        page: 1,
      })
    );
  };

  // Handle type filter
  const handleTypeFilter = (type) => {
    setTypeFilter(type);
    dispatch(
      setEducationFilters({
        page: 1,
      })
    );
  };

  // Clear filters
  const clearFilters = () => {
    setStatusFilter('');
    setCategoryFilter('');
    setTypeFilter('');
    dispatch(
      setEducationFilters({
        search: '',
        page: 1,
      })
    );
  };

  // Handle status change
  const handleStatusChange = async (id, status) => {
    if (!window.confirm(`Are you sure you want to change status to "${status}"?`)) {
      return;
    }

    try {
      setLoadingId(id);
      await updateStatus({ id, status }).unwrap();
      successToast(`Status updated to ${status}`);
      refetch();
    } catch (error) {
      errorToast(error?.data?.message || 'Failed to update status');
    } finally {
      setLoadingId(null);
    }
  };

  // Handle delete
  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) {
      return;
    }

    try {
      setLoadingId(id);
      await deleteScholarship(id).unwrap();
      successToast('Scholarship deleted successfully');
      refetch();
    } catch (error) {
      errorToast(error?.data?.message || 'Failed to delete scholarship');
    } finally {
      setLoadingId(null);
    }
  };

  // Refresh
  const handleRefresh = () => {
    refetch();
    successToast('Data refreshed successfully!');
  };

  // Navigate to student registrations
  const handleViewStudents = () => {
    navigate('/admin/student-registrations');
  };

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <h5 className="mt-3">Loading Scholarships...</h5>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center py-5">
        <div className="alert alert-danger">
          <h5>Something Went Wrong</h5>
          <p>Unable to load scholarships. Please try again.</p>
          <button className="btn btn-primary" onClick={handleRefresh}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* HEADER */}
      {/* <div className="page-header-breadcrumb all_breadcrumb">
        <div className="ms-md-1 mb-1 mb-md-0 ms-0 page_heading_breadcrumb">
          <nav>
            <h4 className="fw-semibold">
              🎓 Education & Scholarships {totalItems > 0 && <span className="badge-custom bg-primary">{totalItems}</span>}
            </h4>
            <ol className="breadcrumb mb-0">
              <li className="breadcrumb-item active">
                <Link to="/admin">Home</Link>
              </li>
              <li className="breadcrumb-item">Education & Scholarships</li>
            </ol>
          </nav>
        </div>
        <div className="page-title fw-semibold fs-18 mb-0">
          {/* <button
            className="btn btn-info me-2"
            onClick={handleViewStudents}
          >
            <FaUsers className="me-1" /> Student Registrations
          </button> 
          <button
            className="btn btn-primary me-2"
            onClick={handleRefresh}
          >
            🔄 Refresh
          </button>
          <button
            className="btn btn-success"
            onClick={() => navigate("/admin/create-scholarship")}
          >
            <FaPlus className="me-1" /> Add Scholarship
          </button>
        </div>
      </div> */}
<CommonBreadcrumb
    title="Education & Scholarships"
    titleIcon={<span className="me-2">🎓</span>}
    count={totalItems}
    breadcrumbs={[
        { label: "Home", link: "/admin" },
        { label: "Education & Scholarships" },
    ]}
    buttons={[
        {
            text: "Refresh",
            icon: <span className="me-1"></span>,
            onClick: handleRefresh,
            className: "btn btn-primary",
        },
        {
            text: "Add Scholarship",
            icon: <FaPlus className="me-1" />,
            link: "/admin/create-scholarship",
            className: "btn btn-success",
        },

        // Agar future me Student Registrations button chahiye ho to uncomment kar sakte hain
        // {
        //     text: "Student Registrations",
        //     icon: <FaUsers className="me-1" />,
        //     onClick: handleViewStudents,
        //     className: "btn btn-info",
        // },
    ]}
/>
   <div className="container-fluid">
       {/* STATS */}
      <div className="row g-3 mb-4">
        <div className="col-md-2 col-6">
          <div className="card card_new_all text-white">
            <div className="card-body text-center">
              <h3 className="mb-0">{stats.total || 0}</h3>
              <small>Total</small>
            </div>
          </div>
        </div>
        <div className="col-md-2 col-6">
          <div className="card card_new_all text-white">
            <div className="card-body text-center">
              <h3 className="mb-0">{stats.pending || 0}</h3>
              <small>Pending</small>
            </div>
          </div>
        </div>
        <div className="col-md-2 col-6">
          <div className="card card_new_all text-white">
            <div className="card-body text-center">
              <h3 className="mb-0">{stats.approved || 0}</h3>
              <small>Approved</small>
            </div>
          </div>
        </div>
        <div className="col-md-2 col-6">
          <div className="card card_new_all text-white">
            <div className="card-body text-center">
              <h3 className="mb-0">{stats.rejected || 0}</h3>
              <small>Rejected</small>
            </div>
          </div>
        </div>
        <div className="col-md-2 col-6">
          <div className="card card_new_all text-white">
            <div className="card-body text-center">
              <h3 className="mb-0">{stats.expired || 0}</h3>
              <small>Expired</small>
            </div>
          </div>
        </div>
        <div className="col-md-2 col-6">
          <div className="card card_new_all text-white">
            <div className="card-body text-center">
              <h3 className="mb-0">{stats.featured || 0}</h3>
              <small>Featured</small>
            </div>
          </div>
        </div>
      </div>

      {/* SEARCH AND FILTER SECTION */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3 align-items-center">
            <div className="col-md-3">
              <SearchBar
                search={filter.search}
                setSearch={handleSearch}
                placeholder="Search by title..."
              />
            </div>

            <div className="col-md-2">
              <select
                className="form-select"
                value={statusFilter}
                onChange={(e) => handleStatusFilter(e.target.value)}
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="expired">Expired</option>
              </select>
            </div>

            <div className="col-md-2">
              <select
                className="form-select"
                value={categoryFilter}
                onChange={(e) => handleCategoryFilter(e.target.value)}
              >
                <option value="">All Categories</option>
                <option value="scholarship">Scholarship</option>
                <option value="education_loan">Education Loan</option>
                <option value="career_guidance">Career Guidance</option>
                <option value="skill_development">Skill Development</option>
                <option value="study_abroad">Study Abroad</option>
              </select>
            </div>

            <div className="col-md-2">
              <select
                className="form-select"
                value={typeFilter}
                onChange={(e) => handleTypeFilter(e.target.value)}
              >
                <option value="">All Types</option>
                <option value="government">Government</option>
                <option value="private">Private</option>
                <option value="trust">Trust</option>
                <option value="corporate">Corporate</option>
                <option value="international">International</option>
              </select>
            </div>

            <div className="col-md-1">
              <select
                className="form-select"
                value={filter.limit || 10}
                onChange={handleLimitChange}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>

            <div className="col-md-2 text-md-end">
              {(filter.search || statusFilter || categoryFilter || typeFilter) && (
                <button
                  className="btn btn-outline-secondary me-2"
                  onClick={clearFilters}
                >
                  <FaTimes className="me-1" />
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Active Filters */}
          {(filter.search || statusFilter || categoryFilter || typeFilter) && (
            <div className="mt-3 d-flex flex-wrap gap-2">
              <span className="text-muted me-2">Active Filters:</span>
              {filter.search && (
                <span className="badge bg-primary d-flex align-items-center gap-1">
                  Search: {filter.search}
                  <button
                    className="btn btn-link btn-sm text-white p-0 ms-1"
                    onClick={() => handleSearch('')}
                  >
                    <FaTimes />
                  </button>
                </span>
              )}
              {statusFilter && (
                <span className="badge bg-info d-flex align-items-center gap-1">
                  Status: {statusFilter}
                  <button
                    className="btn btn-link btn-sm text-white p-0 ms-1"
                    onClick={() => handleStatusFilter('')}
                  >
                    <FaTimes />
                  </button>
                </span>
              )}
              {categoryFilter && (
                <span className="badge bg-success d-flex align-items-center gap-1">
                  Category: {categoryFilter}
                  <button
                    className="btn btn-link btn-sm text-white p-0 ms-1"
                    onClick={() => handleCategoryFilter('')}
                  >
                    <FaTimes />
                  </button>
                </span>
              )}
              {typeFilter && (
                <span className="badge bg-warning text-dark d-flex align-items-center gap-1">
                  Type: {typeFilter}
                  <button
                    className="btn btn-link btn-sm text-dark p-0 ms-1"
                    onClick={() => handleTypeFilter('')}
                  >
                    <FaTimes />
                  </button>
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* TABLE */}
      <div className="st-wrapper-card">
        {scholarships.length === 0 ? (
          <div className="text-center py-5">
            <div className="mb-3">
              <FaGraduationCap className="text-muted" size={48} />
            </div>
            <h5>No Scholarships Found</h5>
            <p className="text-muted">
              {filter.search || statusFilter || categoryFilter || typeFilter
                ? "Try adjusting your search or filter criteria"
                : "No scholarships available"}
            </p>
            {(filter.search || statusFilter || categoryFilter || typeFilter) && (
              <button className="btn btn-primary" onClick={clearFilters}>
                Clear Filters
              </button>
            )}
          </div>
        ) : (
          <div className="st-table-responsive">
            <table className="table st-custom-table align-middle mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Title</th>
                  <th>Category</th>
                  <th>Type</th>
                  <th>Amount</th>
                  <th>Deadline</th>
                  <th>Status</th>
                  <th>Applications</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {scholarships.map((item, index) => (
                  <tr key={item.id}>
                    <td>{(currentPage - 1) * (filter.limit || 10) + index + 1}</td>
                    <td>
                      <div>
                        <div className="fw-semibold">{item.title}</div>
                        <div className="mt-1">
                          {item.is_featured && (
                            <span className="badge bg-warning text-dark me-1">
                              <FaStar className="me-1" /> Featured
                            </span>
                          )}
                          {item.is_verified && (
                            <span className="badge bg-success">
                              <FaCheckCircle className="me-1" /> Verified
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="badge bg-secondary">{getCategoryLabel(item.category)}</span>
                    </td>
                    <td>
                      <span className="badge bg-info text-dark">{getTypeLabel(item.type)}</span>
                    </td>
                    <td>
                      <span className="fw-semibold text-success">{item.amount || 'N/A'}</span>
                    </td>
                    <td>
                      {item.deadline ? (
                        <span className="text-danger">
                          <FaCalendarAlt className="me-1" />
                          {formatDate(item.deadline)}
                        </span>
                      ) : 'N/A'}
                    </td>
                    <td>
                      <div className="status-dropdown">
                        <select
                          className="form-select form-select-sm"
                          value={item.status}
                          onChange={(e) => handleStatusChange(item.id, e.target.value)}
                          disabled={loadingId === item.id}
                          style={{ minWidth: '120px' }}
                        >
                          <option value="pending">Pending</option>
                          <option value="approved">Approved</option>
                          <option value="rejected">Rejected</option>
                          <option value="expired">Expired</option>
                        </select>
                      </div>
                    </td>
                    <td>
                      <span className="badge bg-primary">{item.application_count || 0}</span>
                    </td>
                    <td className="text-center">
                      {/* VIEW */}
                      <button
                        className="st-action-btn st-view-btn me-1"
                        onClick={() =>
                          dispatch(
                            openModal({
                              type: "view-scholarship",
                              title: "View Scholarship",
                              data: item,
                            })
                          )
                        }
                        title="View Details"
                        disabled={loadingId === item.id}
                      >
                        <FaEye />
                      </button>

                      {/* EDIT */}
                      <button
                        className="st-action-btn st-edit-btn me-1"
                        onClick={() => navigate(`/admin/edit-scholarship/${item.id}`)}
                        title="Edit"
                        disabled={loadingId === item.id}
                      >
                        <FaEdit />
                      </button>

                      {/* APPLICATIONS */}
                      <button
                        className="st-action-btn st-status-btn me-1"
                        onClick={() =>
                          dispatch(
                            openModal({
                              type: "view-scholarship-applications",
                              title: `Applications for ${item.title}`,
                              data: { scholarship_id: item.id, title: item.title },
                            })
                          )
                        }
                        title="View Applications"
                        disabled={loadingId === item.id}
                      >
                        <FaUserGraduate />
                      </button>

                      {/* DELETE */}
                      <button
                        className="st-action-btn st-delete-btn"
                        onClick={() => handleDelete(item.id, item.title)}
                        title="Delete"
                        disabled={loadingId === item.id}
                      >
                        {loadingId === item.id ? (
                          <span className="spinner-border spinner-border-sm" />
                        ) : (
                          <FaTrash />
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* PAGINATION */}
        {showPagination && scholarships.length > 0 && (
          <div className="d-flex flex-wrap align-items-center justify-content-between mt-4">
            <div className="text-muted small mb-2 mb-md-0">
              Showing {((currentPage - 1) * (filter.limit || 10)) + 1} to{' '}
              {Math.min(currentPage * (filter.limit || 10), totalItems)} of {totalItems} entries
            </div>
            <Pagination
              page={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
   </div>
    </div>
  );
}

export default Education;